import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { Collapse, IconButton, Popover } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import {
  CharCounter,
  ChoiceCard,
  ChoiceCardGroup,
  ErrorMessage,
  FORM_COLOR,
  FieldBlock,
  GuideLines,
  Label,
  StyledInput,
  TagOption,
  TagSelector,
} from '@/pages/Seminar/components/form/formStyles';
import { TITLE_MAX_LENGTH, removeEmoji } from '@/pages/Seminar/components/form/seminarFormUtils';
import { targetLevels } from '@/pages/Seminar/components/form/seminarFormModel';
import { useSeminarForm } from '@/pages/Seminar/components/form/steps/SeminarFormContext';

// 세미나 유형 비교표 각 행: 기능 라벨 + 일반/커리어 지원 여부 + 상세 설명(? 아이콘 팝오버, 문장마다 줄바꿈)
interface SeminarTypeFeatureRow {
  label: string;
  general: boolean;
  career: boolean;
  detail: string;
}

const seminarTypeFeatureRows: SeminarTypeFeatureRow[] = [
  {
    label: '세미나 카드 노출',
    general: true,
    career: true,
    detail: '일반·커리어 모두 목록과 홈에 세미나 카드로 보여요.\n제목·일정·장소·썸네일을 보여 줘요.',
  },
  {
    label: '진행 방식·일정 선택',
    general: false,
    career: true,
    detail: '커리어는 오프라인·라이브·동영상과 당일·기간·회차 중에서 고를 수 있어요.\n일반은 시작일~종료일과 장소(또는 온라인)만 입력해요.',
  },
  {
    label: '별점·참여자 정보',
    general: false,
    career: true,
    detail: '커리어는 후기 별점과 남은 자리가 카드·상세에 보여요.\n일반은 보이지 않아요.',
  },
  {
    label: '댓글·강사정보',
    general: false,
    career: true,
    detail: '커리어 상세에는 후기·댓글과 강사 프로필(경력·소개)이 보여요.\n일반은 제목·일정·장소·설명만 보여요.',
  },
  {
    label: '강사 다른 세미나 노출',
    general: false,
    career: true,
    detail: '강사 상세의 "강사의 다른 세미나" 목록에는 커리어 세미나만 보여요.',
  },
  {
    label: '참가·결제',
    general: false,
    career: true,
    detail: '커리어는 명치사에서 참가 신청·결제를 받아요.\n일반은 안내용이라 신청 버튼이 없어요.\n접수는 외부에서 받고, 방법은 설명에 적어 주세요.',
  },
  {
    label: '참여자 관리·정산',
    general: false,
    career: true,
    detail: '커리어는 참여자 승인·거절·출석을 관리하고 결제 정산을 받아요.\n일반은 이 기능이 없어요.',
  },
  {
    label: '참여자 알림 (메일·푸시)',
    general: false,
    career: true,
    detail: '커리어는 참가자가 신청·결제하면 강사에게 메일과 푸시로 알려 드려요.\n무료는 강사 승인 후, 유료는 결제하면 바로 참가 확정돼요.\n강사가 시작 전에 거절하면 전액 환불돼요.\n일반은 알림이 없어요.',
  },
];

const TypeMatrixTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: 4px;
  background: #fff;

  th,
  td {
    border: 1px solid ${FORM_COLOR.line};
    padding: 6px 6px;
    text-align: center;
  }

  th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
  }

  td:first-of-type,
  th:first-of-type {
    text-align: left;
    color: #4b5563;
  }
`;

const MatrixToggle = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 2px;
  padding: 2px 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${FORM_COLOR.primaryDeep};

  svg {
    font-size: 18px;
    transition: transform 0.2s;
  }
`;

const GroupName = styled.div`
  margin: 4px 0 0;
  font-size: 12.5px;
  font-weight: 600;
  color: ${FORM_COLOR.muted};
`;

/** 1단계 — 유형(카드 2개)·제목·강사명·분야·대상 */
const StepBasic: React.FC = () => {
  const { control, errors, values, paymentEnabled, seminarType, career, onTypeChange, categoryGroups } = useSeminarForm();
  const [matrixOpen, setMatrixOpen] = useState<boolean>(false);
  const [featurePopover, setFeaturePopover] = useState<{ anchor: HTMLElement; detail: string } | null>(null);
  const titleLength: number = values.title?.length ?? 0;

  return (
    <>
      {/* 세미나 유형 — 결제 기능 ON일 때만 */}
      {paymentEnabled && (
        <FieldBlock data-field="seminarType">
          <Label as="div">세미나 유형<span>*</span></Label>
          <ChoiceCardGroup columns={2} role="radiogroup" aria-label="세미나 유형">
            <ChoiceCard
              type="button"
              role="radio"
              aria-checked={seminarType === 'CAREER'}
              selected={seminarType === 'CAREER'}
              onClick={() => onTypeChange('CAREER')}
            >
              <b>명치사 커리어</b>
              명치사에서 신청·결제·정산까지. 수료가 이력서에 남아요
            </ChoiceCard>
            <ChoiceCard
              type="button"
              role="radio"
              aria-checked={seminarType === 'GENERAL'}
              selected={seminarType === 'GENERAL'}
              onClick={() => onTypeChange('GENERAL')}
            >
              <b>일반</b>
              외부에서 접수하는 세미나를 알리기만 해요
            </ChoiceCard>
          </ChoiceCardGroup>

          {/* 비교표는 기본 접힘 */}
          <MatrixToggle type="button" onClick={() => setMatrixOpen((prev) => !prev)} aria-expanded={matrixOpen}>
            {matrixOpen ? '접기' : '일반과 커리어 차이 보기'}
            <ExpandMoreIcon style={{ transform: matrixOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </MatrixToggle>
          <Collapse in={matrixOpen} timeout="auto" unmountOnExit>
            <TypeMatrixTable>
              <thead>
                <tr>
                  <th>기능</th>
                  <th>일반</th>
                  <th>명치사 커리어</th>
                </tr>
              </thead>
              <tbody>
                {seminarTypeFeatureRows.map((row) => (
                  <tr key={row.label}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                        {row.label}
                        <IconButton
                          size="small"
                          aria-label={`${row.label} 설명`}
                          onClick={(e) => setFeaturePopover({ anchor: e.currentTarget, detail: row.detail })}
                          style={{ padding: 2 }}
                        >
                          <HelpOutlineIcon style={{ fontSize: 16, color: '#9ca3af' }} />
                        </IconButton>
                      </span>
                    </td>
                    <td>{row.general ? '✅' : '❌'}</td>
                    <td>{row.career ? '✅' : '❌'}</td>
                  </tr>
                ))}
              </tbody>
            </TypeMatrixTable>
          </Collapse>

          {/* 열릴 때만 렌더 — 상시 떠 있는 팝오버·dialog 는 스와이프 뒤로가기를 막는다 */}
          {featurePopover && (
            <Popover
              open
              anchorEl={featurePopover.anchor}
              onClose={() => setFeaturePopover(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <div style={{ maxWidth: 300, padding: '14px 16px', fontSize: '0.88rem', lineHeight: 1.6, color: '#374151', whiteSpace: 'pre-line', wordBreak: 'keep-all' }}>
                {featurePopover.detail}
              </div>
            </Popover>
          )}
        </FieldBlock>
      )}

      <FieldBlock>
        <Label htmlFor="seminar-title">
          세미나 제목<span>*</span>
          <small>
            <CharCounter over={titleLength > TITLE_MAX_LENGTH}>{titleLength}/{TITLE_MAX_LENGTH}</CharCounter>
          </small>
        </Label>
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <StyledInput
              {...field}
              id="seminar-title"
              data-field="title"
              aria-invalid={!!errors.title}
              placeholder="예) 보철 실전 케이스 워크숍"
              hasError={!!errors.title}
              // 이모지는 입력 단계에서 뺀다 — 목록 카드 제목 줄높이가 흔들리지 않게.
              // 글자 수는 잘라내지 않는다(한글 조합 중 잘리면 글자가 깨진다) — 카운터와 검증 문구로만 알린다.
              onChange={(e) => field.onChange(removeEmoji(e.target.value))}
            />
          )}
        />
        {errors.title && <ErrorMessage>{errors.title.message}</ErrorMessage>}
        {!errors.title && (values.title?.trim().length ?? 0) > TITLE_MAX_LENGTH && (
          <GuideLines>{`목록 카드에는 두 줄까지만 보여요.\n${TITLE_MAX_LENGTH}자 이내로 줄이는 것을 권해요.`}</GuideLines>
        )}
      </FieldBlock>

      <FieldBlock>
        <Label htmlFor="seminar-instructor">강사명</Label>
        <Controller
          name="instructor"
          control={control}
          render={({ field }) => (
            <StyledInput {...field} id="seminar-instructor" data-field="instructor" hasError={!!errors.instructor} disabled />
          )}
        />
        {errors.instructor && <ErrorMessage>{errors.instructor.message}</ErrorMessage>}
      </FieldBlock>

      {/* 분야·대상 — 커리어만 (일반은 받지 않는다) */}
      {career && (
        <>
          <FieldBlock data-field="categories">
            <Label as="div">
              분야<span>*</span>
              <small>여러 개 고를 수 있어요</small>
            </Label>
            <Controller
              name="categories"
              control={control}
              render={({ field }) => (
                <>
                  {categoryGroups.map((group) => (
                    <div key={group.code} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <GroupName>{group.displayName}</GroupName>
                      <TagSelector>
                        {group.categories.map((cat) => {
                          const current: string[] = field.value || [];
                          const selected: boolean = current.includes(cat.code);
                          return (
                            <TagOption
                              key={cat.code}
                              type="button"
                              selected={selected}
                              aria-pressed={selected}
                              onClick={() => field.onChange(selected ? current.filter((c) => c !== cat.code) : [...current, cat.code])}
                            >
                              {selected && <CheckIcon aria-hidden />}
                              {cat.displayName}
                            </TagOption>
                          );
                        })}
                      </TagSelector>
                    </div>
                  ))}
                </>
              )}
            />
            {errors.categories && <ErrorMessage>{errors.categories.message}</ErrorMessage>}
          </FieldBlock>

          <FieldBlock data-field="level">
            <Label as="div">대상<span>*</span></Label>
            <Controller
              name="level"
              control={control}
              render={({ field }) => (
                <TagSelector role="radiogroup" aria-label="대상">
                  {targetLevels.map((level) => (
                    <TagOption
                      key={level.type}
                      type="button"
                      role="radio"
                      aria-checked={field.value === level.type}
                      selected={field.value === level.type}
                      onClick={() => field.onChange(level.type)}
                    >
                      {level.description}
                    </TagOption>
                  ))}
                </TagSelector>
              )}
            />
            {errors.level && <ErrorMessage>{errors.level.message}</ErrorMessage>}
          </FieldBlock>
        </>
      )}
    </>
  );
};

export default StepBasic;
